import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft } from 'lucide-react';
import { NAV_GROUPS } from './Sidebar';
import { useRpc } from '../../lib/queries';
import { classMeta } from '../../lib/classes';
import { Badge } from '../ui/Badge';

interface DirRow {
  id: string; email: string | null; full_name: string | null; university: string | null;
  class: string; created_at: string;
}

/** cmd+K: jump to any page, or find a user by email, name or id. */
export const CommandPalette: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => { if (!open) { setQ(''); setDebounced(''); } }, [open]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data: dir, isFetching } = useRpc<{ rows: DirRow[] }>(
    'admin_dash_users',
    { p_search: debounced, p_page: 1, p_per: 6 },
    { enabled: open && debounced.length >= 2 },
  );
  const users = dir?.rows;

  const pages = useMemo(() => {
    const all = NAV_GROUPS.flatMap(g => g.items.map(i => ({ ...i, group: g.label })));
    if (!q.trim()) return all.slice(0, 8);
    return all.filter(i => i.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter') {
        if (debounced.length >= 2 && users && users.length > 0) {
          nav(`/users/${users[0].id}`);
          onClose();
        } else if (pages.length > 0) {
          nav(pages[0].to);
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, nav, pages, users, debounced]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4">
      <div className="absolute inset-0 bg-stone-900/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-lg border border-stone-200 shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 h-12 border-b border-stone-100">
          <Search className="w-4 h-4 text-stone-400" />
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search users by email or name, or jump to a page"
            className="flex-1 h-full text-[13px] text-stone-900 focus:outline-none placeholder:text-stone-400"
          />
          <span className="text-[11px] text-stone-400 flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> open first</span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {debounced.length >= 2 && (
            <div className="mb-1">
              <p className="px-2 py-1 text-[11.5px] font-medium text-stone-500">Users {isFetching ? '(searching)' : ''}</p>
              {(users ?? []).map(u => (
                <button
                  key={u.id}
                  onClick={() => { nav(`/users/${u.id}`); onClose(); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-stone-100 text-left"
                >
                  <span className="text-[13px] text-stone-800 truncate flex-1">
                    {u.full_name || u.email || u.id.slice(0, 8)}
                    {u.full_name && u.email && <span className="text-stone-400"> · {u.email}</span>}
                  </span>
                  <Badge tone={classMeta(u.class).tone}>{classMeta(u.class).label}</Badge>
                </button>
              ))}
              {users && users.length === 0 && !isFetching && (
                <p className="px-2 py-1.5 text-[12px] text-stone-400">No users match</p>
              )}
            </div>
          )}
          <p className="px-2 py-1 text-[11.5px] font-medium text-stone-500">Pages</p>
          {pages.map(pItem => {
            const Icon = pItem.icon;
            return (
              <button
                key={pItem.to}
                onClick={() => { nav(pItem.to); onClose(); }}
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-stone-100 text-left"
              >
                <Icon className="w-4 h-4 text-stone-400" />
                <span className="text-[13px] text-stone-800">{pItem.label}</span>
                <span className="text-[11px] text-stone-400">{(pItem as { group?: string }).group}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
